<template>
  <div>
    <BaseTable
      :rows="rows"
      :columns="columns"
      idKey="id"
      searchable
      :selectable="false"
      :loading="loading"
      :pagination="pagination"
      server-sort
      initial-sort-key="id"
      initial-sort-order="desc"
      @changePage="onPageChange"
      @updatePageSize="onPageSizeChange"
      @searchChanged="onSearchChanged"
      @sortChanged="onSortChanged"
    >
      <template #buttons>
        <select class="form-select form-select-sm" style="width:auto;" v-model="statusFilter" @change="fetchLeads(1)">
          <option value="">All Statuses</option>
          <option value="discovered">Discovered</option>
          <option value="raw_stored">Raw Stored</option>
          <option value="ai_processed">AI Processed</option>
          <option value="do_not_contact">Do Not Contact</option>
          <option value="failed">Failed</option>
        </select>
        <select class="form-select form-select-sm" style="width:auto;" v-model="contactedFilter" @change="fetchLeads(1)">
          <option value="">All Contact Statuses</option>
          <option value="yes">Contacted</option>
          <option value="no">Not Contacted</option>
        </select>
      </template>

      <template #col-status="{ row }">
        <span class="badge bg-gradient-secondary" style="font-size:11px;">{{ row.status }}</span>
      </template>
      <template #col-timezone="{ row }">
        {{ row.target_location?.timezone || '—' }}
      </template>
      <template #col-contacts="{ row }">
        {{ (row.contacts || []).length }}
      </template>
      <template #col-contacted="{ row }">
        <span class="badge" :class="hasContacted(row) ? 'bg-gradient-success' : 'bg-gradient-secondary'" style="font-size:11px;">
          {{ hasContacted(row) ? 'Yes' : 'No' }}
        </span>
      </template>
      <template #actions="{ row }">
        <button class="btn btn-sm btn-outline-info mb-0 py-1 px-2 me-1" title="Edit" @click="openEdit(row)">
          <i class="material-icons text-sm">edit</i>
        </button>
        <button class="btn btn-sm btn-outline-danger mb-0 py-1 px-2" title="Delete" @click="deleteLead(row)">
          <i class="material-icons text-sm">delete</i>
        </button>
      </template>
    </BaseTable>

    <ContentModal v-model="editOpen" title="Edit Lead" size="lg">
      <div v-if="editing" class="row g-3">
        <div class="col-md-6">
          <MaterialInput v-model="editing.name" label="Organization Name" />
        </div>
        <div class="col-md-6">
          <MaterialInput v-model="editing.website_url" label="Website URL" />
        </div>
        <div class="col-md-6">
          <MaterialInput v-model="editing.denomination" label="Denomination" />
        </div>
        <div class="col-md-6">
          <MaterialSelect v-model="editing.status" label="Status" :options="statusOptions" />
        </div>
        <div class="col-md-6">
          <MaterialSelect v-model="editing.lead_quality_score" label="Lead Quality" :options="qualityOptions" />
        </div>
        <div class="col-12">
          <label class="form-label">AI Mission Summary</label>
          <textarea class="form-control" rows="3" v-model="editing.ai_mission_summary"></textarea>
        </div>

        <div class="col-12" v-if="editing.contacts && editing.contacts.length">
          <h6 class="mt-2">Contacts</h6>
          <div v-for="contact in editing.contacts" :key="contact.id" class="row g-2 align-items-center border-bottom pb-2 mb-2">
            <div class="col-md-3"><MaterialInput v-model="contact.full_name" label="Full Name" /></div>
            <div class="col-md-3"><MaterialInput v-model="contact.title_role" label="Title / Role" /></div>
            <div class="col-md-3"><MaterialInput v-model="contact.email" label="Email" /></div>
            <div class="col-md-2">
              <MaterialSelect v-model="contact.status" label="Status" :options="contactStatusOptions" />
            </div>
            <div class="col-md-1 d-flex align-items-end justify-content-end">
              <button type="button" class="btn btn-sm btn-outline-danger mb-0 py-1 px-2" title="Delete Contact" :disabled="deletingContactId === contact.id" @click="deleteContact(contact)">
                <i class="material-icons text-sm">delete</i>
              </button>
            </div>
          </div>
        </div>
        <div class="col-12 border-top pt-3 mt-2">
          <h6>Add Contact</h6>
          <div class="row g-2">
            <div class="col-md-3"><MaterialInput v-model="newContact.full_name" label="Full Name" /></div>
            <div class="col-md-3"><MaterialInput v-model="newContact.title_role" label="Title / Role" /></div>
            <div class="col-md-4"><MaterialInput v-model="newContact.email" label="Email" /></div>
            <div class="col-md-2 d-flex align-items-end"><MaterialButton type="button" color="info" size="sm" :disabled="addingContact" @click="addContact">{{ addingContact ? 'Adding...' : 'Add Contact' }}</MaterialButton></div>
          </div>
        </div>
      </div>

      <template #footer>
        <MaterialButton type="button" color="secondary" @click="editOpen = false">Cancel</MaterialButton>
        <MaterialButton type="button" color="success" :disabled="saving" @click="save">
          {{ saving ? 'Saving...' : 'Save' }}
        </MaterialButton>
      </template>
    </ContentModal>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import BaseTable from '@/components/Table/Table.vue'
import ContentModal from '@/components/Modal/ContentModal.vue'
import MaterialInput from '@/components/common/MaterialInput.vue'
import MaterialSelect from '@/components/common/MaterialSelect.vue'
import MaterialButton from '@/components/common/MaterialButton.vue'
import api from '@/config/api'

const loading = ref(true)
const rows = ref([])
const searchTerm = ref('')
const statusFilter = ref('')
const contactedFilter = ref('')
const sortBy = ref('id')
const sortOrder = ref('desc')
const editOpen = ref(false)
const editing = ref(null)
const saving = ref(false)
const deletingContactId = ref(null)
const addingContact = ref(false)
const newContact = reactive({ full_name: '', title_role: '', email: '' })

const pagination = ref({
  currentPage: 1,
  pageSize: 10,
  totalRecords: 0,
  totalPages: 0,
  hasPrevious: false,
  hasNext: false,
})

const columns = [
  { key: 'id', label: 'ID' },
  { key: 'name', label: 'NAME' },
  { key: 'website_url', label: 'WEBSITE' },
  { key: 'denomination', label: 'DENOMINATION' },
  { key: 'lead_quality_score', label: 'QUALITY' },
  { key: 'status', label: 'STATUS' },
  { key: 'timezone', label: 'TIMEZONE' },
  { key: 'contacts', label: 'CONTACTS' },
  { key: 'contacted', label: 'CONTACTED' },
]

const statusOptions = [
  { value: 'discovered', label: 'Discovered' },
  { value: 'raw_stored', label: 'Raw Stored' },
  { value: 'ai_processed', label: 'AI Processed' },
  { value: 'do_not_contact', label: 'Do Not Contact' },
  { value: 'failed', label: 'Failed' },
]

const qualityOptions = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
]

const contactStatusOptions = [
  { value: 'pending', label: 'Pending' },
  { value: 'emailed', label: 'Emailed' },
  { value: 'replied', label: 'Replied' },
  { value: 'bounced', label: 'Bounced' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
]

function hasContacted(row) {
  return (row.contacts || []).some((contact) => String(contact.status || '').toLowerCase() !== 'pending')
}

async function fetchLeads(page = 1, pageSize = pagination.value.pageSize) {
  loading.value = true
  try {
    const response = await api.get('/admin/leads', {
      params: {
        page_number: page,
        page_size: pageSize,
        search: searchTerm.value,
        status: statusFilter.value,
        contacted: contactedFilter.value,
        sort_by: sortBy.value,
        sort_order: sortOrder.value,
      },
    })
    rows.value = response.data?.output || []
    const pag = response.data?.pagination || {}
    pagination.value = {
      currentPage: pag.current_page || 1,
      pageSize: pag.page_size || pageSize,
      totalRecords: pag.total_records || 0,
      totalPages: pag.total_pages || 0,
      hasPrevious: pag.has_previous || false,
      hasNext: pag.has_next || false,
    }
  } catch (e) {
    console.error('Error fetching leads:', e)
    rows.value = []
  } finally {
    loading.value = false
  }
}

function onPageChange(page) { fetchLeads(page) }
function onPageSizeChange(size) { fetchLeads(1, size) }
function onSearchChanged(term) { searchTerm.value = term || ''; fetchLeads(1) }
function onSortChanged({ key, order }) {
  sortBy.value = key || 'id'
  sortOrder.value = order === 'asc' ? 'asc' : 'desc'
  fetchLeads(1)
}

function trimValue(value) {
  return typeof value === 'string' ? value.trim() : value
}

function openEdit(row) {
  editing.value = JSON.parse(JSON.stringify(row))
  editOpen.value = true
}

async function addContact() {
  const contactPayload = {
    full_name: trimValue(newContact.full_name),
    title_role: trimValue(newContact.title_role),
    email: trimValue(newContact.email),
  }
  if (!editing.value || !contactPayload.email) return
  addingContact.value = true
  try {
    const response = await api.post(`/admin/leads/${editing.value.id}/contacts`, contactPayload)
    editing.value.contacts = [...(editing.value.contacts || []), response.data?.output]
    newContact.full_name = ''
    newContact.title_role = ''
    newContact.email = ''
  } catch (e) {
    console.error('Error adding contact:', e)
  } finally {
    addingContact.value = false
  }
}

async function save() {
  if (!editing.value) return
  saving.value = true
  try {
    await api.put(`/admin/leads/${editing.value.id}`, {
      name: trimValue(editing.value.name),
      website_url: trimValue(editing.value.website_url),
      denomination: editing.value.denomination,
      ai_mission_summary: editing.value.ai_mission_summary,
      status: editing.value.status,
      lead_quality_score: editing.value.lead_quality_score,
      contacts: (editing.value.contacts || []).map((c) => ({
        id: c.id,
        full_name: trimValue(c.full_name),
        first_name: c.first_name,
        title_role: c.title_role,
        email: trimValue(c.email),
        status: c.status,
      })),
    })
    editOpen.value = false
    await fetchLeads(pagination.value.currentPage)
  } catch (e) {
    console.error('Error saving lead:', e)
  } finally {
    saving.value = false
  }
}

async function deleteLead(row) {
  if (!window.confirm(`Delete lead "${row.name}" and all of its contacts? This cannot be undone.`)) return
  try {
    await api.delete(`/admin/leads/${row.id}`)
    await fetchLeads(pagination.value.currentPage)
  } catch (e) {
    console.error('Error deleting lead:', e)
  }
}

async function deleteContact(contact) {
  if (!editing.value) return
  if (!window.confirm(`Delete contact "${contact.full_name || contact.email}"? This cannot be undone.`)) return
  deletingContactId.value = contact.id
  try {
    await api.delete(`/admin/leads/${editing.value.id}/contacts/${contact.id}`)
    editing.value.contacts = editing.value.contacts.filter((c) => c.id !== contact.id)
  } catch (e) {
    console.error('Error deleting contact:', e)
  } finally {
    deletingContactId.value = null
  }
}

onMounted(() => fetchLeads())
</script>
